import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { AssessmentRunOption } from '../../shared/utils';
import SocInnerForm from '../SocInnerForm';
import type { EditSocFormData } from '../SocPerformanceFormConfig';
import { baseSocFormSchema } from '../SocPerformanceFormConfig';
import type { SocPerformanceRowData } from '../SocPerformanceTab';
import type { UpdateSocPerformanceResultMutation } from './UpdatePerformanceSocResult.api';
import { UpdateSocPerformanceResultDocument } from './UpdatePerformanceSocResult.api';
import client from '@/apolloClient';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import DatePickerField from '@/components/fields/DatePickerField';
import SelectField from '@/components/fields/SelectField';
import SwitchField from '@/components/fields/SwitchField';
import { useGetCompanyId } from '@/contexts/UserSession';
import { mapPassFailWithNullToResult, mapResultToPassFailWithNull, splitSeconds } from '@/utils/common';
import { success as showSuccessNotification, error as showErrorNotification } from '@/utils/notifications/actions';
import '@/utils/notifications';

interface EditPerformanceSocModalProps {
    open: boolean;
    onClose: (refresh?: boolean) => void;
    rowData: SocPerformanceRowData | null;
    assessmentRunOptions: AssessmentRunOption[];
}

const EditPerformanceSocModal = ({ open, onClose, rowData, assessmentRunOptions }: EditPerformanceSocModalProps) => {
    const { t } = useTranslation('performanceResult');
    const selectedCompanyId = useGetCompanyId();

    const { minutes: timingMinutes, seconds: timingSeconds } = splitSeconds(rowData?.timingSec);

    const form = useForm<EditSocFormData>({
        mode: 'onChange',
        resolver: zodResolver(baseSocFormSchema),
        defaultValues: {
            timingSec: rowData?.timingSec ?? undefined,
            result: mapResultToPassFailWithNull(rowData?.result),
            date: rowData?.assessmentDate ?? undefined,
            timingSecMinutes: timingMinutes,
            timingSecSeconds: timingSeconds,
            assessmentRunId: rowData?.assessmentRunId ?? undefined,
            isTiedToAssessmentRun: rowData?.isTiedToAssessmentRun ?? false,
        },
    });

    const isTiedToAssessmentRun = form.watch('isTiedToAssessmentRun');

    useEffect(() => {
        if (rowData?.isTiedToAssessmentRun && rowData.assessmentRunId) {
            form.setValue('assessmentRunId', rowData.assessmentRunId);
        }
    }, [form, rowData]);

    const handleSubmit = async () => {
        if (!(await form.trigger()) || !rowData) {
            return;
        }
        const values = form.getValues();

        try {
            const { data } = await client.mutate<UpdateSocPerformanceResultMutation>({
                mutation: UpdateSocPerformanceResultDocument,
                variables: {
                    id: rowData.id,
                    input: {
                        companyId: selectedCompanyId || '',
                        assessmentRunId: values.isTiedToAssessmentRun ? values.assessmentRunId || undefined : undefined,
                        result: {
                            timingSec: values.timingSec ?? undefined,
                            result: mapPassFailWithNullToResult(values.result),
                        },
                        completedAt: values.date ?? undefined,
                    },
                },
            });

            if (data?.updateSocResult?.__typename === 'ErrorResponse') {
                const errorResponse = data.updateSocResult;
                const specificErrorField = errorResponse.fields?.find(f => f.message);
                const errorMessage = specificErrorField?.message || t('updateError.description', { type: 'SOC' });

                showErrorNotification({
                    message: t('updateError.title', { type: 'SOC' }),
                    description: errorMessage,
                });

                throw new Error(errorMessage);
            } else {
                showSuccessNotification({
                    message: t('updateSuccess.title', { type: 'SOC' }),
                    description: t('updateSuccess.message', { type: 'SOC' }),
                });

                onClose(true);
            }
        } catch (error) {
            console.error('Error updating SOC performance result:', error);
            throw error;
        }
    };

    const modalFooter = (
        <div className="mt-5 flex w-full justify-end">
            <div className="flex space-x-2">
                <Button outline onClick={() => onClose()}>
                    {t('soc.editModal.cancelBtn')}
                </Button>
                <Button color="teal" onClick={handleSubmit}>
                    {t('soc.editModal.saveBtn')}
                </Button>
            </div>
        </div>
    );

    return (
        <FormProvider {...form}>
            <Modal
                open={open}
                onClose={onClose}
                title={t('soc.editModal.title')}
                footer={modalFooter}
                showCloseButton={false}
                className="w-full sm:w-xl"
                largeTitle
            >
                <form onSubmit={handleSubmit}>
                    <div className="grid gap-4 sm:grid-cols-3">
                        <div className="sm:col-span-3">
                            <DatePickerField name="date" label={t('soc.form.assessmentDate')} required />
                        </div>
                        <SocInnerForm />
                        <div className="sm:col-span-3">
                            <SwitchField
                                name="isTiedToAssessmentRun"
                                label={t('soc.form.tieToAssessmentRun', {
                                    defaultValue: 'Tie To Assessment Run',
                                })}
                            />
                        </div>
                        {isTiedToAssessmentRun && (
                            <div className="sm:col-span-3">
                                <SelectField
                                    name="assessmentRunId"
                                    label={t('filter.assessmentRun')}
                                    placeholder={t('filter.assessmentRunPlaceholder')}
                                    options={assessmentRunOptions}
                                    required
                                />
                            </div>
                        )}
                    </div>
                </form>
            </Modal>
        </FormProvider>
    );
};

export default EditPerformanceSocModal;
